import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  Heart,
  Home,
  LifeBuoy,
  LogOut,
  Menu,
  MessageCircle,
  Search,
  Settings,
  Shield,
  Sparkles,
  User,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MemberAvatar from "@/components/common/MemberAvatar";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/sponsors", label: "Browse Sponsors" },
  { to: "/messages", label: "Messages", auth: true },
  { to: "/favorites", label: "Favorites", auth: true },
  { to: "/become-a-sponsor", label: "Become a Sponsor" },
  { to: "/pricing", label: "Pricing" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "About" },
];

export default function TopNav() {
  const { user, logout, unreadNotifications } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);

  const items = NAV.filter((n) => !n.auth || user);

  // Close both menus on every real navigation. NavLink already closes the
  // mobile menu on click, but this also covers the browser back/forward
  // buttons, which previously left the panel open on top of the new page and
  // made the page underneath look unresponsive to taps.
  useEffect(() => {
    setOpen(false);
    setMenu(false);
  }, [location.pathname, location.key]);

  // Lock background scrolling while the mobile menu is open so a tap aimed at
  // a menu item cannot land on content that has scrolled underneath it.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-[#E3DED4]">
      <div className="bg-[#1F2A2E] text-[#F7F4EF] text-xs">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-center gap-2 text-center">
          <LifeBuoy className="w-3.5 h-3.5 shrink-0" />
          <span>In crisis or immediate danger?</span>
          <Link
            to="/resources"
            data-testid="crisis-banner-link"
            className="inline-flex items-center min-h-[36px] px-1 underline font-semibold hover:text-white touch-manipulation"
          >
            Need Immediate Help?
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="h-16 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 shrink-0 min-h-[44px] touch-manipulation"
          >
            <span className="w-9 h-9 rounded-xl bg-[#1F6F78] text-white flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </span>
            <span className="font-display text-xl font-bold text-[#1F2A2E]">Sponsor Me</span>
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-[#D7EEF0] text-[#1F6F78]"
                      : "text-[#4B5A60] hover:bg-[#F2EFE8] hover:text-[#1F2A2E]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link
                  to="/notifications"
                  data-testid="nav-notifications"
                  className="relative p-2 rounded-lg hover:bg-[#F2EFE8]"
                  aria-label="Notifications"
                >
                  <Bell className="w-5 h-5 text-[#4B5A60]" />
                  {unreadNotifications > 0 && (
                    <span
                      data-testid="notification-badge"
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D07A4A] text-white text-[10px] font-bold flex items-center justify-center"
                    >
                      {unreadNotifications}
                    </span>
                  )}
                </Link>
                <div className="relative">
                  <button
                    onClick={() => setMenu((v) => !v)}
                    data-testid="profile-menu-button"
                    className="flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-[#F2EFE8] border border-[#E3DED4] bg-white"
                  >
                    <MemberAvatar user={user} size={30} />
                    <span className="hidden sm:block text-sm font-semibold text-[#1F2A2E] max-w-[110px] truncate">
                      {user.display_name}
                    </span>
                  </button>
                  {menu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
                      <div className="absolute right-0 mt-2 w-60 z-50 bg-white border border-[#E3DED4] rounded-xl shadow-lg overflow-hidden">
                        <div className="px-4 py-3 bg-[#F7F4EF] border-b border-[#E3DED4]">
                          <p className="text-sm font-semibold text-[#1F2A2E] truncate">
                            {user.display_name}
                          </p>
                          <p className="text-xs text-[#4B5A60] truncate">{user.email}</p>
                          <p className="text-xs mt-1 font-semibold text-[#1F6F78]">
                            {user.access_label}
                          </p>
                        </div>
                        {[
                          { to: "/dashboard", label: "My Dashboard", icon: Home },
                          ...(user.account_type === "sponsor"
                            ? [{ to: "/sponsor-dashboard", label: "Sponsor Dashboard", icon: Heart }]
                            : []),
                          { to: "/settings", label: "Profile & Settings", icon: Settings },
                          ...(user.is_admin
                            ? [{ to: "/admin", label: "Admin Dashboard", icon: Shield }]
                            : []),
                        ].map((l) => (
                          <Link
                            key={l.to}
                            to={l.to}
                            onClick={() => setMenu(false)}
                            data-testid={`menu-${l.to.replace("/", "")}`}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#1F2A2E] hover:bg-[#F2EFE8]"
                          >
                            <l.icon className="w-4 h-4 text-[#4B5A60]" />
                            {l.label}
                          </Link>
                        ))}
                        <button
                          onClick={() => {
                            setMenu(false);
                            logout();
                            navigate("/");
                          }}
                          data-testid="logout-button"
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#B42318] hover:bg-[#FBE6E4] border-t border-[#E3DED4]"
                        >
                          <LogOut className="w-4 h-4" /> Sign out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  data-testid="nav-login-button"
                  className="hidden sm:inline-flex text-[#1F2A2E] hover:bg-[#F2EFE8]"
                >
                  Sign in
                </Button>
                {/* Registration must be reachable from the header on a phone,
                    not only from inside the hamburger menu. Short label on
                    small screens so it cannot squash the logo. */}
                <Button
                  onClick={() => navigate("/register")}
                  data-testid="nav-register-button"
                  className="bg-[#1F6F78] hover:bg-[#185B62] active:bg-[#134B52] text-white h-11 px-3 sm:px-4 text-sm touch-manipulation"
                >
                  <span className="sm:hidden">Join free</span>
                  <span className="hidden sm:inline">Create free account</span>
                </Button>
              </div>
            )}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-button"
              className="xl:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg hover:bg-[#F2EFE8] active:bg-[#E6E1D8] touch-manipulation focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F6F78]"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-nav-panel"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          id="mobile-nav-panel"
          data-testid="mobile-nav-panel"
          className="xl:hidden bg-white border-t border-[#E3DED4] px-4 py-3 max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain"
        >
          <nav aria-label="Main menu" className="flex flex-col gap-1">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center min-h-[48px] px-3 py-3 rounded-lg text-base font-semibold touch-manipulation transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F6F78] ${
                    isActive
                      ? "bg-[#D7EEF0] text-[#1F6F78]"
                      : "text-[#1F2A2E] hover:bg-[#F2EFE8] active:bg-[#E6E1D8]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {!user && (
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  data-testid="mobile-nav-login-button"
                  className="flex-1 h-12 bg-white touch-manipulation"
                  onClick={() => {
                    setOpen(false);
                    navigate("/login");
                  }}
                >
                  Sign in
                </Button>
                <Button
                  data-testid="mobile-nav-register-button"
                  className="flex-1 h-12 bg-[#1F6F78] hover:bg-[#185B62] active:bg-[#134B52] text-white touch-manipulation"
                  onClick={() => {
                    setOpen(false);
                    navigate("/register");
                  }}
                >
                  Join free
                </Button>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

export function BottomNav() {
  const { user } = useAuth();
  const tabs = [
    { to: "/", label: "Home", icon: Home },
    { to: "/sponsors", label: "Sponsors", icon: Search },
    { to: "/messages", label: "Messages", icon: MessageCircle },
    { to: "/favorites", label: "Favorites", icon: Heart },
    { to: user ? "/dashboard" : "/login", label: "Profile", icon: User },
  ];
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E3DED4] pb-[env(safe-area-inset-bottom)]">
      <div className="grid grid-cols-5">
        {tabs.map((t) => (
          <NavLink
            key={t.label}
            to={t.to}
            end={t.to === "/"}
            data-testid={`bottomnav-${t.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 min-h-[56px] py-2 text-[11px] font-semibold touch-manipulation transition-colors duration-150 active:bg-[#F2EFE8] ${
                isActive ? "text-[#1F6F78]" : "text-[#7A868B]"
              }`
            }
          >
            <t.icon className="w-5 h-5" />
            {t.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
